// main.js — 起動・入力配線・ゲームループ

import '../css/style.css';
import { Game } from './game.js';
import { World } from './world.js';
import { Editor } from './editor.js';
import { findMachine, DEFAULT_MACHINE, currencyOf, CURRENCIES, machineName } from './machines.js';
import {
  CUSTOM_SLOTS, newCustomMachine, sanitize, toRecord, attachVideos, releaseVideos,
} from './custom.js';
import {
  listMachines, saveMachine, deleteMachine, putVideo, videoKey, isPersistent,
} from './storage.js';
import { findPrize, prizeName, sanitizeCollection, BALLS_PER_ING } from './prizes.js';
import { Tutorial, TUTORIAL_KEY } from './tutorial.js';
import {
  loadLocale, localeFromPath, findLocale, DEFAULT_LOCALE, PREF_KEY, LOCALES,
  t, num, locale, localeHref,
} from './i18n.js';

// ---------------------------------------------------------------- 言語
// 何よりも先に文字列を読む。ここから下は全部 t() を通して組み立てるので、
// 読み終わる前に画面を作ると鍵がそのまま出てしまう。
const activeLocale = await loadLocale(
  window.__LOCALE__ || localeFromPath() || DEFAULT_LOCALE);
document.documentElement.lang = findLocale(activeLocale)?.html || 'ja';
try { localStorage.setItem(PREF_KEY, activeLocale); } catch { /* 保存できなくても動く */ }

/** data-i18n を付けた要素に文字を流し込む（ビルド時にも同じことをしている） */
function applyStaticText(root = document) {
  for (const el of root.querySelectorAll('[data-i18n]')) el.textContent = t(el.dataset.i18n);
  for (const el of root.querySelectorAll('[data-i18n-title]')) {
    const s2 = t(el.dataset.i18nTitle);
    el.title = s2;
    if (el.hasAttribute('aria-label')) el.setAttribute('aria-label', s2);
  }
  for (const el of root.querySelectorAll('[data-i18n-aria]')) {
    el.setAttribute('aria-label', t(el.dataset.i18nAria));
  }
}
applyStaticText();

const $ = (id) => document.getElementById(id);

const hud = {
  root: $('app'),
  stock: $('st-stock'),
  stockLabel: $('st-stock-label'),
  jackpots: $('st-jackpots'),
  spins: $('st-spins'),
  pending: $('st-pending'),
};

const canvas = $('board');
const machineTag = $('machine-tag');

// ---------------------------------------------------------------- 保存データ

const SAVE_KEY = 'pachinkorokoro.save.v1';

function loadSave() {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (typeof d?.wallet !== 'number' && typeof d?.wallets?.ball !== 'number') return null;
    return d;
  } catch { return null; }
}

function persist() {
  commitStock();
  try {
    localStorage.setItem(SAVE_KEY, JSON.stringify({
      wallet: wallets.ball,          // 旧版と読み合わせるために残す
      wallets,
      lastMachine: game.machine.id,
      stats,
      collection,
    }));
  } catch { /* プライベートモードなどでは黙って諦める */ }
}

const saved = loadSave();
const stats = saved?.stats ?? {};          // 機種ごとの { spins, jackpots }
const collection = sanitizeCollection(saved?.collection);   // 景品の所持数

// 通貨ごとの財布。正規の台は玉、自作台はメダルで遊ぶので勘定を分ける。
const wallets = {
  ball: saved?.wallets?.ball ?? saved?.wallet ?? CURRENCIES.ball.start,
  medal: saved?.wallets?.medal ?? CURRENCIES.medal.start,
  ing: saved?.wallets?.ing ?? 0,
};

// 自作台。添字が島のスロット番号
const customs = new Array(CUSTOM_SLOTS).fill(undefined);

const game = new Game(canvas, hud, findMachine(saved?.lastMachine ?? DEFAULT_MACHINE.id));
game.stock = wallets[currencyOf(game.machine).id];

// 動作確認や調整用に本体を触れるようにしておく
window.pachinko = game;

// ---------------------------------------------------------------- 財布と成績

/**
 * いま座っている台の残りを、その通貨の財布へ書き戻す。
 * 台を離れている間は財布のほうが正しい（景品交換などで動く）ので、
 * 止まっている盤面の値で上書きしないよう黙って見送る。
 */
function commitStock() {
  if (testing || worldOpen || editorOpen) return;
  wallets[currencyOf(game.machine).id] = game.stock;
}

function currentStats() {
  const id = game.machine.id;
  const s = stats[id] || (stats[id] = { spins: 0, jackpots: 0 });
  return s;
}

// 台に座っている間の成績を、その台の通算へ足し込む
let sessionBase = { spins: 0, jackpots: 0 };
function commitStats() {
  if (testing) return;                     // 試し打ちは通算に入れない
  const s = currentStats();
  s.spins += Math.max(0, game.spins - sessionBase.spins);
  s.jackpots += Math.max(0, game.jackpots - sessionBase.jackpots);
  sessionBase = { spins: game.spins, jackpots: game.jackpots };
}

function applyMachine(m) {
  machineTag.textContent = (testing ? t('testing.tag') : '') + machineName(m);
  document.documentElement.style.setProperty('--machine', m.color);
}

// 台を離れるとき、自作台が抱えている映像の URL を解放する
function unloadMachine() {
  if (game.machine.custom) releaseVideos(game.machine);
}

// ---------------------------------------------------------------- 画面の切り替え

// 歩き回っている間やエディタを開いている間は盤面を止める。タブ復帰で勝手に
// 再開しないよう、停止理由をまとめて 1 か所で判断する。
let worldOpen = false;
let editorOpen = false;
let testing = null;             // 試し打ち中 {from: 'editor'}
function syncPaused() { game.setPaused(worldOpen || editorOpen || document.hidden); }

const world = new World($('world'), {
  onPick: (m) => seatAt(m),
  onEdit: (m, slot) => openEditor(m || newCustomMachine(slot)),
  onDelete: (m) => discardCustom(m),
  onTrade: (kind, payload) => trade(kind, payload),
  onGuide: () => tutorial.greet(!tutorialDone),
}, game.audio);

// ---------------------------------------------------------------- 案内係

// 一度最後まで見たかどうか。初回だけ挨拶の入りかたを変える
let tutorialDone = false;
try { tutorialDone = localStorage.getItem(TUTORIAL_KEY) === '1'; } catch { /* 無くても動く */ }

const tutorial = new Tutorial($('app'), {
  world: () => world,
  onFinish: (done) => {
    if (!done) return;
    tutorialDone = true;
    try { localStorage.setItem(TUTORIAL_KEY, '1'); } catch { /* 覚えられなくても実害はない */ }
  },
});
window.pachinko.tutorial = tutorial;   // 動作確認用

/** 台に着席する */
async function seatAt(m) {
  commitStats();
  commitStock();
  worldOpen = false;
  editorOpen = false;
  if (m.id !== game.machine.id || testing) {
    unloadMachine();
    if (m.custom) await attachVideos(m);
    testing = null;
    game.loadMachine(m, false);
    sessionBase = { spins: 0, jackpots: 0 };
  }
  game.stock = wallets[currencyOf(m).id];
  applyMachine(m);
  persist();
  world.hide();
  editor.close();
  syncPaused();
}

function openWorld() {
  hold(false);
  commitStats();
  persist();
  world.setState(wallets, stats, customs, collection);
  world.show();
  worldOpen = true;
  editorOpen = false;
  editor.close();
  syncPaused();
}

window.pachinko.world = world;   // 動作確認用
window.pachinko.customs = customs;
window.pachinko.wallets = wallets;
window.pachinko.collection = collection;
applyMachine(game.machine);

// ---------------------------------------------------------------- 景品と換金

/**
 * 玉 →（ホールのカウンター）→ 景品 →（店の外の交換所）→ イン
 * →（ホールのカウンター）→ 玉 という一周。
 * 財布とコレクションはここが持ち、world.js は結果を表示するだけにしてある。
 */
function trade(kind, payload) {
  const done = (msg) => {
    persist();
    world.setState(wallets, stats, customs, collection);
    return { ok: true, msg };
  };

  if (kind === 'buy-prize') {
    const p = findPrize(payload.prizeId);
    if (!p) return { ok: false, msg: t('trade.noPrize') };
    if (wallets.ball < p.cost) {
      return { ok: false, msg: t('trade.needBalls', { n: num(p.cost - wallets.ball) }) };
    }
    wallets.ball -= p.cost;
    collection[p.id] = (collection[p.id] || 0) + 1;
    return done(t('trade.bought', { name: prizeName(p) }));
  }

  if (kind === 'cash-in') {
    const p = findPrize(payload.prizeId);
    const have = collection[payload.prizeId] || 0;
    const n = Math.min(Math.max(1, Math.floor(payload.n) || 1), have);
    if (!p || !have) return { ok: false, msg: t('trade.notOwned') };
    collection[p.id] = have - n;
    if (!collection[p.id]) delete collection[p.id];
    wallets.ing += p.ing * n;
    return done(t('trade.cashed', { name: prizeName(p), n, ing: num(p.ing * n) }));
  }

  if (kind === 'buy-balls') {
    const ing = Math.floor(payload.ing) || 0;
    if (ing <= 0) return { ok: false, msg: t('trade.pickAmount') };
    if (wallets.ing < ing) return { ok: false, msg: t('trade.needIng') };
    wallets.ing -= ing;
    wallets.ball += ing * BALLS_PER_ING;
    return done(t('trade.lent', { n: num(ing * BALLS_PER_ING) }));
  }

  return { ok: false, msg: t('trade.unavailable') };
}

// ---------------------------------------------------------------- 自作台

const editor = new Editor($('editor'), {
  onExit: () => { editorOpen = false; openWorld(); },
  onSave: (m, files) => storeCustom(m, files),
  onTest: (m, files) => testDrive(m, files),
});

window.pachinko.editor = editor;   // 動作確認用

function openEditor(machine) {
  hold(false);
  commitStats();
  persist();
  world.hide();
  worldOpen = false;
  editorOpen = true;
  editor.open(machine);
  syncPaused();
}

/** 編集した台を保存する。映像はここで初めて IndexedDB へ書く */
async function storeCustom(m, files = {}) {
  try {
    for (const [state, file] of Object.entries(files)) {
      await putVideo(videoKey(m.id, state), file);
    }
    const record = toRecord(m);
    await saveMachine(record);
    customs[m.slot] = sanitize(record);
    // 打っている最中の台を編集した場合は、その場で差し替える
    if (game.machine.id === m.id) await seatAt(customs[m.slot]);
    alert(t(isPersistent() ? 'save.ok' : 'save.okVolatile', { name: m.name }));
  } catch (e) {
    console.error(e);
    alert(t('save.fail'));
  }
}

async function discardCustom(m) {
  commitStats();
  commitStock();
  await deleteMachine(m.id);
  customs[m.slot] = undefined;
  if (game.machine.id === m.id) {
    unloadMachine();
    game.loadMachine(DEFAULT_MACHINE, false);
    game.stock = wallets.ball;
    applyMachine(DEFAULT_MACHINE);
  }
  world.setState(wallets, stats, customs, collection);
}

/** 保存前の下書きをその場で打ってみる。メダルは減らない */
async function testDrive(draft, files) {
  commitStats();
  commitStock();
  unloadMachine();
  const m = sanitize(toRecord(draft));
  await attachVideos(m, files);
  testing = { from: 'editor' };
  game.loadMachine(m, false);
  game.stock = 2000;
  sessionBase = { spins: 0, jackpots: 0 };
  applyMachine(m);
  editor.close();
  editorOpen = false;
  worldOpen = false;
  world.hide();
  syncPaused();
}

/** 試し打ちをやめてエディタへ戻る */
function backToEditor() {
  hold(false);
  unloadMachine();
  testing = null;
  game.loadMachine(DEFAULT_MACHINE, false);
  game.stock = wallets.ball;
  applyMachine(game.machine);
  sessionBase = { spins: 0, jackpots: 0 };
  editorOpen = true;
  worldOpen = false;
  if (!editor.reopen()) openWorld();
  syncPaused();
}

// 保存済みの自作台を読み込む。読み終わるまでは島が空に見えるだけで実害はない
listMachines().then((rows) => {
  for (const raw of rows) {
    const m = sanitize(raw);
    if (m.slot >= 0 && m.slot < CUSTOM_SLOTS) customs[m.slot] = m;
  }
  if (worldOpen) world.setState(wallets, stats, customs, collection);
}).catch(() => { /* 保存領域が無くても遊べる */ });

// ---------------------------------------------------------------- 発射操作

const lever = $('lever');
let holding = false;

function hold(on) {
  if (holding === on) return;
  holding = on;
  game.setFiring(on);
  lever.classList.toggle('on', on);
}

lever.addEventListener('pointerdown', (e) => {
  e.preventDefault();
  lever.setPointerCapture?.(e.pointerId);
  hold(true);
});
for (const ev of ['pointerup', 'pointercancel', 'pointerleave']) {
  lever.addEventListener(ev, () => hold(false));
}
lever.addEventListener('contextmenu', (e) => e.preventDefault());

// キーボード: スペースで発射、上下で強度
window.addEventListener('keydown', (e) => {
  if (editorOpen) {
    if (editor.onKeyDown(e)) e.preventDefault();
    return;
  }
  if (worldOpen) {
    if (world.onKeyDown(e)) e.preventDefault();
    return;
  }
  if (e.repeat) return;
  if (e.code === 'Space') { e.preventDefault(); hold(true); }
  if (e.code === 'ArrowUp') { e.preventDefault(); nudgePower(+4); }
  if (e.code === 'ArrowDown') { e.preventDefault(); nudgePower(-4); }
});
window.addEventListener('keyup', (e) => {
  if (editorOpen) return;
  if (worldOpen) { if (world.onKeyUp(e)) e.preventDefault(); return; }
  if (e.code === 'Space') { e.preventDefault(); hold(false); }
});
window.addEventListener('blur', () => hold(false));

// ---------------------------------------------------------------- 強度

const power = $('power');
const powerVal = $('power-val');

function syncPower() {
  const dial = Number(power.value) / 100;
  game.setPower(dial);
  powerVal.textContent = t(`side.${game.side}`);
  powerVal.className = 'side-' + game.side;
}
power.addEventListener('input', syncPower);
function nudgePower(d) {
  power.value = String(Math.max(0, Math.min(100, Number(power.value) + d)));
  syncPower();
}
syncPower();

// ---------------------------------------------------------------- ツール

// 言語切り替え。行き先は同じ場所の別ディレクトリ（/ja/ → /ko/）なので、
// 読み込み直しになる。選んだ言語は覚えて、次に / へ来たときの判定より優先する。
const langSel = $('lang');
langSel.innerHTML = LOCALES.map((l) =>
  `<option value="${l.id}"${l.id === locale() ? ' selected' : ''}>${l.label}</option>`).join('');
langSel.addEventListener('change', () => {
  const id = langSel.value;
  if (id === locale()) return;
  try { localStorage.setItem(PREF_KEY, id); } catch { /* 保存できなくても飛べる */ }
  persist();
  location.href = localeHref(id);
});

const btnSound = $('btn-sound');
btnSound.addEventListener('click', () => {
  const muted = game.toggleMute();
  btnSound.textContent = muted ? '🔈' : '🔊';
  btnSound.classList.toggle('off', muted);
});

$('btn-reset').addEventListener('click', () => {
  if (confirm(t('reset.confirm'))) {
    for (const k of Object.keys(stats)) delete stats[k];
    for (const k of Object.keys(collection)) delete collection[k];
    sessionBase = { spins: 0, jackpots: 0 };
    testing = null;
    wallets.ball = CURRENCIES.ball.start;
    wallets.medal = CURRENCIES.medal.start;
    wallets.ing = 0;
    unloadMachine();
    game.loadMachine(DEFAULT_MACHINE, false);
    game.stock = wallets.ball;
    applyMachine(game.machine);
    persist();
    openWorld();
  }
});

// 試し打ち中はエディタへ、それ以外は歩いていた場所へ戻る
$('btn-hall').addEventListener('click', () => {
  if (testing) backToEditor(); else openWorld();
});

// ---------------------------------------------------------------- 起動

const boot = $('boot');
let entered = false;
$('boot-start').addEventListener('click', () => {
  game.audio.init();
  boot.classList.add('gone');
  entered = true;
  openWorld();          // 入店したらまず台選び
});

// ---------------------------------------------------------------- ループ

// 実時間に合わせて 60fps 相当まで追いつかせる（表示レートに依らず同じ速さで動く）
const STEP = 1000 / 60;
let last = performance.now();
let acc = 0;

function frame(now) {
  requestAnimationFrame(frame);
  let dt = now - last;
  last = now;
  if (dt > 250) dt = STEP;          // タブ復帰時に一気に進めない
  acc += dt;

  // 案内は画面をまたいで続くので、どの画面でも毎フレーム見る
  tutorial.tick({
    worldOpen, editorOpen, game,
    seated: entered && !worldOpen && !editorOpen,
  });

  let guard = 0;
  // エディタは変化があったときだけ自分で描くので、ここでは回さない
  while (acc >= STEP && guard++ < 5) {
    if (editorOpen) { acc = 0; break; }
    if (worldOpen) world.update(); else game.update();
    acc -= STEP;
  }
  if (editorOpen) return;
  if (worldOpen) world.render(); else game.render();
}
requestAnimationFrame(frame);

document.addEventListener('visibilitychange', () => {
  syncPaused();
  if (document.hidden) { hold(false); commitStats(); persist(); }
  else last = performance.now();
});

window.addEventListener('pagehide', () => { commitStats(); persist(); });
setInterval(persist, 10000);

// ---------------------------------------------------------------- PWA
// Service Worker の登録は vite-plugin-pwa が index.html へ注入する

const btnInstall = $('btn-install');
let installPrompt = null;
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  installPrompt = e;
  btnInstall.classList.remove('hidden');
});
btnInstall.addEventListener('click', async () => {
  if (!installPrompt) return;
  installPrompt.prompt();
  await installPrompt.userChoice;
  installPrompt = null;
  btnInstall.classList.add('hidden');
});
window.addEventListener('appinstalled', () => btnInstall.classList.add('hidden'));
